import { useEffect, useState, type PropsWithChildren } from "react";

import { useCampaign } from "hooks/api/use-campaigns";
import { useCurrentClientId } from "hooks/api/use-clients";
import { useEmailWriterCompletionPolling } from "hooks/use-email-writer-completion-polling";

import { getActiveEmailWriterPollingCampaignIds } from "utilities/email-writer-polling-session";

const EmailWriterCampaignPoller = ({
  clientId,
  campaignId,
}: {
  clientId: string;
  campaignId: string;
}) => {
  const { data: campaign } = useCampaign({ clientId, id: campaignId });

  useEmailWriterCompletionPolling({
    clientId,
    campaignId,
    campaignName: campaign?.name ?? "Campaign",
    enabled: Boolean(campaign),
  });

  return null;
};

export const ClientEmailWriterPollingProvider = ({ children }: PropsWithChildren) => {
  const clientId = useCurrentClientId();
  const [pollingCampaignIds, setPollingCampaignIds] = useState<string[]>([]);

  useEffect(() => {
    if (!clientId) {
      setPollingCampaignIds([]);
      return;
    }

    const syncSessions = () => {
      setPollingCampaignIds(getActiveEmailWriterPollingCampaignIds(clientId));
    };

    syncSessions();

    window.addEventListener("focus", syncSessions);
    window.addEventListener("email-writer-polling-session-changed", syncSessions);

    return () => {
      window.removeEventListener("focus", syncSessions);
      window.removeEventListener("email-writer-polling-session-changed", syncSessions);
    };
  }, [clientId]);

  return (
    <>
      {clientId &&
        pollingCampaignIds.map((campaignId) => (
          <EmailWriterCampaignPoller key={campaignId} clientId={clientId} campaignId={campaignId} />
        ))}
      {children}
    </>
  );
};
